if (!customElements.get('lazy-video')) {
  class LazyVideo extends HTMLElement {
    constructor() {
      super();
      this.hydrated = false;
      this.shadow = this.attachShadow({ mode: 'open' });

      this.styleEl = document.createElement('style');
      this.styleEl.textContent = `
        :host { display:block }
        .frame { position:relative; width:100%; aspect-ratio:var(--aspect,16/9); overflow:hidden }
        video, .poster, .play-btn { position:absolute; inset:0; width:100%; height:100% }
        video, .poster { object-fit:cover }
        video { z-index:1 }
        .poster { z-index:2 }
        .poster[hidden] { display:none }
        .play-btn {
          z-index:3; display:flex; align-items:center; justify-content:center;
          cursor:pointer; user-select:none;
          display:none;
        }
        .play-btn[hidden] { display:none }
        .play-pill {
          padding:.6rem 1rem; border-radius:9999px; background:#000c; color:#fff;
          font:500 14px/1 system-ui,-apple-system,Segoe UI,Roboto,sans-serif;
        }
        video.hidden { display:none }
        @media(min-width:768px) {
          video.md\\:block { display:block }
          video.md\\:hidden { display:none }
        }
      `;

      this.frame = document.createElement('div');
      this.frame.className = 'frame';

      this.posterEl = document.createElement('img');
      this.posterEl.className = 'poster';

      this.playBtn = document.createElement('div');
      this.playBtn.className = 'play-btn';
      this.playBtn.innerHTML = '<span class="play-pill">Play</span>';

      this.frame.append(this.posterEl, this.playBtn);
      this.shadow.append(this.styleEl, this.frame);

      this.onVisibilityChange = () => (document.hidden ? this.pauseAll() : this.maybeAutoplayVisible());
      this.io = new IntersectionObserver(entries => {
        const entry = entries[0];
        if (!entry) return;
        if (entry.isIntersecting) {
          if (this.shouldAutoplay()) {
            this.hydrateIfNeeded();
            this.maybeAutoplayVisible();
          }
        } else {
          this.pauseAll();
        }
      }, { rootMargin: '200px 0px', threshold: [0, 0.15] });

      this.onPlayClick = () => this.userPlayVisible();
    }

    connectedCallback() {
      this.applyPosterAttributes();
      this.applyLoadingAttributes();
      this.maybePrefetchPoster();
      this.playBtn.hidden = this.shouldAutoplay();
      this.io.observe(this);
      document.addEventListener('visibilitychange', this.onVisibilityChange, { passive: true });
      this.playBtn.addEventListener('click', this.onPlayClick);
    }

    disconnectedCallback() {
      this.io.disconnect();
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      this.playBtn.removeEventListener('click', this.onPlayClick);
    }

    shouldAutoplay() {
      return (this.getAttribute('autoplay') || '').toLowerCase() === 'true';
    }

    getPrefetchMode() {
      return (this.getAttribute('prefetch') || 'none').toLowerCase();
    }

    applyPosterAttributes() {
      const src = this.getAttribute('poster-src') || '';
      const w = parseInt(this.getAttribute('poster-width') || '', 10);
      const h = parseInt(this.getAttribute('poster-height') || '', 10);
      if (w > 0 && h > 0) this.style.setProperty('--aspect', `${w}/${h}`);
      if (src) this.posterEl.src = src;
      this.posterEl.alt = this.getAttribute('poster-alt') || '';
    }

    applyLoadingAttributes() {
      const loadingAttr = this.getAttribute('poster-loading') || this.getAttribute('loading');
      const fetchPriority = this.getAttribute('poster-fetchpriority') || this.getAttribute('fetchpriority');
      const loading = loadingAttr ? loadingAttr : 'lazy';
      this.posterEl.loading = loading;
      if (fetchPriority) this.posterEl.setAttribute('fetchpriority', fetchPriority);
    }

    maybePrefetchPoster() {
      const pmode = this.getPrefetchMode();
      if (pmode === 'poster' || pmode === 'all') {
        const src = this.posterEl.currentSrc || this.posterEl.src;
        if (src) this.createHeadLinkOnce('preload', 'image', src, this.posterEl.getAttribute('fetchpriority') || null);
      }
    }

    maybePrefetchVideoSources() {
      if (!this.shouldAutoplay()) return;
      const pmode = this.getPrefetchMode();
      if (pmode !== 'video' && pmode !== 'all') return;
      this.videos().forEach(v => {
        const sources = Array.from(v.querySelectorAll('source')).map(s => s.src).filter(Boolean);
        if (v.src) sources.push(v.src);
        sources.forEach(src => this.createHeadLinkOnce('preload', 'video', src, null));
      });
    }

    createHeadLinkOnce(rel, as, href, fetchPriority) {
      const head = document.head || document.getElementsByTagName('head')[0];
      if (!head || !href) return;
      const exists = head.querySelector(`link[rel="${rel}"][as="${as}"][href="${CSS.escape(href)}"]`);
      if (exists) return;
      const link = document.createElement('link');
      link.rel = rel;
      link.as = as;
      link.href = href;
      if (fetchPriority) link.setAttribute('fetchpriority', fetchPriority);
      head.appendChild(link);
    }

    hydrateIfNeeded() {
      if (this.hydrated) return;
      const tpl = this.querySelector('template');
      if (!tpl) return;
      this.frame.appendChild(tpl.content.cloneNode(true));
      tpl.remove();
      this.videos().forEach(v => {
        v.autoplay = false;
        v.preload = 'none';
        v.muted = true;
        v.playsInline = true;
        const hideOverlays = () => { this.posterEl.hidden = true; this.playBtn.hidden = true; };
        v.addEventListener('playing', hideOverlays, { once: true });
      });
      this.maybePrefetchVideoSources();
      this.hydrated = true;
    }

    videos() {
      return Array.from(this.shadow.querySelectorAll('video'));
    }

    visibleVideos() {
      return this.videos().filter(v => v.offsetParent !== null && v.getClientRects().length > 0);
    }

    maybeAutoplayVisible() {
      if (!this.shouldAutoplay()) return;
      this.visibleVideos().forEach(v => {
        if (!v.dataset.loaded) { v.preload = 'auto'; v.load(); v.dataset.loaded = '1'; }
        const p = v.play();
        if (p && p.catch) p.catch(() => {});
      });
    }

    userPlayVisible() {
      if (!this.hydrated) this.hydrateIfNeeded();
      this.visibleVideos().forEach(v => {
        v.controls = true;
        if (!v.dataset.loaded) { v.preload = 'auto'; v.load(); v.dataset.loaded = '1'; }
        const p = v.play();
        if (p && p.catch) p.catch(() => {});
      });
    }

    play() {
      this.hydrateIfNeeded();
      const vids = this.visibleVideos();
      vids.forEach(v => {
        if (!v.dataset.loaded) { v.preload = 'auto'; v.load(); v.dataset.loaded = '1'; }
        const p = v.play();
        if (p && p.catch) p.catch(() => {});
      });
      if (vids.length) { this.posterEl.hidden = true; this.playBtn.hidden = true; }
    }

    pause() {
      this.pauseAll();
    }

    reset() {
      this.videos().forEach(v => { v.pause(); v.currentTime = 0; });
      this.posterEl.hidden = false;
      if (!this.shouldAutoplay()) this.playBtn.hidden = false;
    }

    pauseAll() {
      this.videos().forEach(v => { if (!v.paused) v.pause(); });
      if (!this.shouldAutoplay()) this.playBtn.hidden = false;
    }
  }

  customElements.define('lazy-video', LazyVideo);
}
